import { useState, useEffect, useRef } from "react";
import axios, { API } from "../../api";
import { startRecording, attachSilenceDetector, playBase64Mp3 } from "../../voiceUtils";

/**
 * Full-screen voice "Call with Aura" mode.
 * - User taps the orb to start a continuous open-mic conversation.
 * - VAD detects ~1.4s of silence -> sends audio -> Aura replies -> resumes listening.
 * - Tap the orb again to end the call.
 */
export function VoiceCallModal({ onClose, voice, wallpaper, mood, onMoodUpdate, onWallpaperRefresh }) {
  const [state, setState] = useState("idle"); // idle | listening | thinking | speaking
  const [transcript, setTranscript] = useState("");
  const [reply, setReply] = useState("");
  const [level, setLevel] = useState(0);
  const [error, setError] = useState("");
  const stopRecRef = useRef(null);
  const stopVadRef = useRef(null);
  const cancelledRef = useRef(false);
  const sessionActiveRef = useRef(false);

  useEffect(() => () => endCall(), []); // cleanup on unmount

  const sendAudio = async (blob) => {
    if (cancelledRef.current) return;
    setState("thinking");
    try {
      const form = new FormData();
      const ext = (blob.type.includes("mp4") || blob.type.includes("aac") || blob.type.includes("m4a"))
        ? "m4a" : (blob.type.includes("ogg") ? "ogg" : "webm");
      form.append("audio", blob, `clip.${ext}`);
      if (voice) form.append("voice", voice);

      const res = await axios.post(`${API}/voice/chat`, form, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      if (cancelledRef.current) return;

      setTranscript(res.data.transcript || "");
      setReply(res.data.response || "");

      if (res.data.mood && onMoodUpdate) {
        onMoodUpdate({ mood: res.data.mood, emoji: res.data.mood_emoji });
      }

      if (res.data.audio_base64) {
        setState("speaking");
        try { await playBase64Mp3(res.data.audio_base64); } catch (_) {}
      }

      if (res.data.mood_shifted && onWallpaperRefresh) {
        onWallpaperRefresh();
      }

      if (sessionActiveRef.current && !cancelledRef.current) {
        startListening();
      } else {
        setState("idle");
      }
    } catch (e) {
      console.error("voice chat error", e);
      const msg = e.response?.data?.detail || "Something went wrong";
      setError(msg);
      setState("idle");
      sessionActiveRef.current = false;
    }
  };

  const startListening = async () => {
    setError("");
    try {
      const rec = await startRecording();
      stopRecRef.current = rec;
      setState("listening");
      setLevel(0);
      // VAD: when 1.4s of silence detected, stop recording & send
      let stopped = false;
      const stopVad = attachSilenceDetector(rec.stream, {
        threshold: 0.012,
        silenceMs: 1400,
        onLevel: (rms) => setLevel(rms),
        onSilence: async () => {
          if (stopped) return;
          stopped = true;
          stopVadRef.current = null;
          const blob = await rec.stop();
          stopRecRef.current = null;
          // skip tiny clips (no real speech captured)
          if (blob.size < 2000) {
            if (sessionActiveRef.current && !cancelledRef.current) startListening();
            else setState("idle");
            return;
          }
          sendAudio(blob);
        }
      });
      stopVadRef.current = stopVad;
    } catch (e) {
      console.error("mic error", e);
      setError("Microphone permission denied or unavailable.");
      setState("idle");
      sessionActiveRef.current = false;
    }
  };

  const startCall = () => {
    cancelledRef.current = false;
    sessionActiveRef.current = true;
    startListening();
  };

  const endCall = () => {
    cancelledRef.current = true;
    sessionActiveRef.current = false;
    if (stopVadRef.current) { try { stopVadRef.current(); } catch (_) {} stopVadRef.current = null; }
    if (stopRecRef.current) { try { stopRecRef.current.cancel(); } catch (_) {} stopRecRef.current = null; }
    setState("idle");
  };

  const handleOrbTap = () => {
    if (state === "idle") startCall();
    else endCall();
  };

  const stateLabel =
    state === "listening" ? "Listening..."
    : state === "thinking" ? "Aura is thinking..."
    : state === "speaking" ? "Aura is speaking..."
    : "Tap the orb to talk";

  // Scale orb based on mic level when listening
  const orbScale = state === "listening" ? 1 + Math.min(level * 6, 0.6) : 1;

  return (
    <div className="voice-call-overlay" data-testid="voice-call-modal">
      {wallpaper && (
        <img
          src={`data:image/png;base64,${wallpaper}`}
          alt=""
          className="voice-call-bg"
        />
      )}
      <div className="voice-call-content">
        <button
          className="voice-call-close"
          onClick={() => { endCall(); onClose(); }}
          data-testid="voice-call-close-btn"
          aria-label="End call"
        >
          ×
        </button>

        <div className="voice-call-mood">
          <span>{mood?.emoji || "✨"}</span>
        </div>

        <div className="voice-call-title">
          <h2>Aura</h2>
          <p className="voice-call-subtitle">{stateLabel}</p>
        </div>

        <button
          className={`voice-orb voice-orb-${state}`}
          onClick={handleOrbTap}
          style={{ transform: `scale(${orbScale})` }}
          data-testid="voice-orb"
          aria-label={state === "idle" ? "Start voice call" : "End voice call"}
        >
          <div className="voice-orb-glow"></div>
          <div className="voice-orb-pulse"></div>
          <div className="voice-orb-icon">
            {state === "idle" ? "🎙️" : state === "thinking" ? "✨" : state === "speaking" ? "🔊" : "👂"}
          </div>
        </button>

        {transcript && (
          <div className="voice-call-transcript" data-testid="voice-transcript">
            <strong>You:</strong> {transcript}
          </div>
        )}
        {reply && (
          <div className="voice-call-reply" data-testid="voice-reply">
            <strong>Aura:</strong> {reply}
          </div>
        )}

        {error && <div className="voice-call-error" data-testid="voice-error">{error}</div>}

        <p className="voice-call-hint">
          {state === "idle" ? "Tap once to start a conversation. Aura will listen, respond, and keep listening." : "Tap the orb again to end the call."}
        </p>
      </div>
    </div>
  );
}
